import React, { useMemo } from "react";
import "./JarvisOrb.css";

/**
 * JarvisOrb — 3 swappable styles:
 *   - "constellation": wire-mesh sphere (vertices + edges) — sparse, structural
 *   - "nebula":        dense particle cloud forming a sphere — many bright dots
 *   - "solid":         the classic glowing gold ball (legacy fallback)
 *
 * Voice-switchable via `set_orb_style` action. Style is read from
 * localStorage("rawco_orb_style"); falls back to "constellation".
 */
export default function JarvisOrb({ active = false, listening = false, style }) {
  const chosen = (style || (typeof window !== "undefined" && localStorage.getItem("rawco_orb_style")) || "constellation").toLowerCase();

  return (
    <div
      className={`rc-orb-stage rc-orb-style-${chosen} ${active ? "is-active" : ""} ${listening ? "is-listening" : ""}`}
      data-testid="jarvis-orb-canvas"
      data-style={chosen}
    >
      <div className="rc-orb-grid" />
      <div className="rc-orb-glow" />

      {chosen === "solid" && (
        <div className="rc-orb-core">
          <div className="rc-orb-inner" />
          <div className="rc-orb-flare" />
          <div className="rc-orb-flare rc-orb-flare-2" />
        </div>
      )}

      {chosen === "constellation" && <ConstellationSphere />}
      {chosen === "nebula" && <NebulaSphere />}
      {chosen === "matrix" && <MatrixSphere />}
      {chosen === "plasma" && <PlasmaSphere />}

      {/* Concentric tick rings (kept for sci-fi flavour) */}
      <svg className="rc-orb-ticks" viewBox="-200 -200 400 400" preserveAspectRatio="xMidYMid meet">
        <circle cx="0" cy="0" r="170" fill="none" stroke="#ffb800" strokeOpacity="0.10" strokeDasharray="1 10" />
        <circle cx="0" cy="0" r="185" fill="none" stroke="#ffb800" strokeOpacity="0.20" />
        {Array.from({ length: 36 }).map((_, i) => {
          const a = (i * 360) / 36;
          const x1 = Math.cos((a * Math.PI) / 180) * 185;
          const y1 = Math.sin((a * Math.PI) / 180) * 185;
          const x2 = Math.cos((a * Math.PI) / 180) * 192;
          const y2 = Math.sin((a * Math.PI) / 180) * 192;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ffb800" strokeOpacity="0.45" strokeWidth="1" />;
        })}
      </svg>
    </div>
  );
}

/* ---------- Constellation: wire-frame sphere with vertices + edges ---------- */
function ConstellationSphere() {
  // Generate Fibonacci-spiral sphere points (well-distributed)
  const RADIUS = 140;
  const POINTS = 220;
  const pts = useMemo(() => {
    const arr = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < POINTS; i++) {
      const y = 1 - (i / (POINTS - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = golden * i;
      arr.push({
        x: Math.cos(theta) * r * RADIUS,
        y: y * RADIUS,
        z: Math.sin(theta) * r * RADIUS,
      });
    }
    return arr;
  }, []);

  // Pre-compute "front-facing" neighbour pairs (short edges only) so the wire-mesh isn't a hairball
  const edges = useMemo(() => {
    const out = [];
    const MAX_LEN = RADIUS * 0.45;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, dz = pts[i].z - pts[j].z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < MAX_LEN * MAX_LEN) out.push([i, j, Math.sqrt(d2)]);
      }
    }
    return out;
  }, [pts]);

  return (
    <svg className="rc-orb-mesh rc-orb-spin" viewBox="-200 -200 400 400" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="orbNodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff5cc" stopOpacity="1" />
          <stop offset="60%" stopColor="#ffb800" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#ffb800" stopOpacity="0" />
        </radialGradient>
        <filter id="orbBloom" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Edges */}
      <g filter="url(#orbBloom)">
        {edges.map(([i, j, d], k) => {
          const a = pts[i], b = pts[j];
          // Depth opacity — points facing camera (positive z) brighter
          const depth = ((a.z + b.z) / 2 + RADIUS) / (2 * RADIUS); // 0..1
          const alpha = 0.04 + depth * 0.30;
          return (
            <line
              key={k}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke="#ffd34d" strokeOpacity={alpha} strokeWidth={0.5}
            />
          );
        })}
      </g>

      {/* Nodes */}
      <g filter="url(#orbBloom)">
        {pts.map((p, i) => {
          const depth = (p.z + RADIUS) / (2 * RADIUS); // 0..1
          const size = 0.7 + depth * 2.1;
          const alpha = 0.25 + depth * 0.75;
          // Sparkle a handful of bright stars
          const bright = i % 11 === 0;
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={size} fill="#ffd34d" fillOpacity={alpha}>
                <animate attributeName="fill-opacity"
                  values={`${alpha * 0.6};${alpha};${alpha * 0.6}`}
                  dur={`${2 + (i % 5) * 0.6}s`}
                  repeatCount="indefinite"
                  begin={`${(i % 7) * 0.18}s`} />
              </circle>
              {bright && (
                <circle cx={p.x} cy={p.y} r={size * 3.2} fill="url(#orbNodeGlow)" opacity={depth * 0.8} />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/* ---------- Nebula: dense particle cloud forming a sphere ---------- */
function NebulaSphere() {
  const RADIUS = 150;
  const POINTS = 420;
  const pts = useMemo(() => {
    const arr = [];
    for (let i = 0; i < POINTS; i++) {
      // Spherical sampling with slight outward drift for the halo
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = RADIUS * (0.78 + Math.random() * 0.32); // 0.78..1.10 — some halo escape
      arr.push({
        x: r * Math.sin(phi) * Math.cos(theta),
        y: r * Math.cos(phi),
        z: r * Math.sin(phi) * Math.sin(theta),
        // bright stars are sparser
        bright: Math.random() < 0.08,
        seed: Math.random(),
      });
    }
    return arr;
  }, []);

  return (
    <svg className="rc-orb-mesh rc-orb-spin-slow" viewBox="-200 -200 400 400" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="orbStarGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff5cc" stopOpacity="1" />
          <stop offset="50%" stopColor="#ffd34d" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#ffb800" stopOpacity="0" />
        </radialGradient>
        <filter id="nebBloom" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.9" />
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g filter="url(#nebBloom)">
        {pts.map((p, i) => {
          const depth = (p.z + RADIUS) / (2 * RADIUS);
          const r = (p.bright ? 1.3 : 0.55) + depth * 1.5;
          const alpha = 0.25 + depth * 0.75;
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={r} fill="#ffd34d" fillOpacity={alpha}>
                <animate attributeName="fill-opacity"
                  values={`${alpha * 0.4};${alpha};${alpha * 0.4}`}
                  dur={`${1.6 + p.seed * 3}s`}
                  repeatCount="indefinite"
                  begin={`${p.seed * 1.4}s`} />
              </circle>
              {p.bright && (
                <circle cx={p.x} cy={p.y} r={r * 3.5} fill="url(#orbStarGlow)" opacity={depth * 0.7} />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/* ---------- Matrix: characters scrolling on a spherical shell ---------- */
function MatrixSphere() {
  const RADIUS = 150;
  // Use Katakana + hex chars for that classic Matrix feel
  const CHARS = "ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ0123456789ABCDEF";
  const POINTS = 260;
  const pts = useMemo(() => {
    const arr = [];
    for (let i = 0; i < POINTS; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      arr.push({
        x: RADIUS * Math.sin(phi) * Math.cos(theta),
        y: RADIUS * Math.cos(phi),
        z: RADIUS * Math.sin(phi) * Math.sin(theta),
        ch: CHARS[Math.floor(Math.random() * CHARS.length)],
        flip: 1.4 + Math.random() * 3.2, // seconds between character flips
        bright: Math.random() < 0.07,
      });
    }
    return arr;
  }, []);

  return (
    <svg className="rc-orb-mesh rc-orb-spin" viewBox="-200 -200 400 400" preserveAspectRatio="xMidYMid meet">
      <defs>
        <filter id="matrixGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="0.5" />
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {pts.map((p, i) => {
        const depth = (p.z + RADIUS) / (2 * RADIUS);
        const alpha = 0.18 + depth * 0.78;
        const size = 5 + depth * 6;
        const colour = p.bright ? "#ffec80" : "#ffd34d";
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            fill={colour}
            fillOpacity={alpha}
            fontSize={size}
            fontFamily="'Share Tech Mono', monospace"
            textAnchor="middle"
            dominantBaseline="middle"
            filter="url(#matrixGlow)"
          >
            {p.ch}
            <animate
              attributeName="fill-opacity"
              values={`${alpha * 0.3};${alpha};${alpha * 0.3}`}
              dur={`${p.flip}s`}
              repeatCount="indefinite"
              begin={`${(i % 9) * 0.18}s`}
            />
          </text>
        );
      })}
    </svg>
  );
}

/* ---------- Plasma: AI-particle orb with swirling tendrils ---------- */
function PlasmaSphere() {
  const RADIUS = 140;
  const POINTS = 360;
  const pts = useMemo(() => {
    const arr = [];
    for (let i = 0; i < POINTS; i++) {
      // Distribute on TWO interlocking rings + sphere core for a plasma look
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const ringBias = Math.random() < 0.35 ? 0.95 : 0.65 + Math.random() * 0.45;
      const r = RADIUS * ringBias;
      arr.push({
        x: r * Math.sin(phi) * Math.cos(theta),
        y: r * Math.cos(phi),
        z: r * Math.sin(phi) * Math.sin(theta),
        seed: Math.random(),
        size: 0.6 + Math.random() * 1.6,
      });
    }
    return arr;
  }, []);

  return (
    <svg className="rc-orb-mesh rc-orb-spin-fast" viewBox="-200 -200 400 400" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="plasmaCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffec80" stopOpacity="0.95" />
          <stop offset="40%" stopColor="#ff9d00" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ff5500" stopOpacity="0" />
        </radialGradient>
        <filter id="plasmaBloom" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.4" />
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx="0" cy="0" r="60" fill="url(#plasmaCore)" />
      <g filter="url(#plasmaBloom)">
        {pts.map((p, i) => {
          const depth = (p.z + RADIUS) / (2 * RADIUS);
          const alpha = 0.18 + depth * 0.78;
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={p.size + depth * 1.6}
              fill={depth > 0.7 ? "#fff5cc" : "#ffb800"}
              fillOpacity={alpha}
            >
              <animate attributeName="fill-opacity"
                values={`${alpha * 0.3};${alpha};${alpha * 0.3}`}
                dur={`${0.9 + p.seed * 2.4}s`}
                repeatCount="indefinite"
                begin={`${p.seed * 1.2}s`} />
              <animate attributeName="r"
                values={`${p.size};${p.size + 0.8};${p.size}`}
                dur={`${1.4 + p.seed * 2}s`}
                repeatCount="indefinite" />
            </circle>
          );
        })}
      </g>
    </svg>
  );
}
